import React, { Component, type MouseEvent, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  MerchantGroupLogo,
  MerchantGroupMerchants,
  MerchantGroupStatus,
} from "src/merchantgroups/formatters/MerchantGroupFormatters";
import type { MerchantGroup } from "src/merchantgroups/objects/MerchantGroup";
import { getMerchantGroupDetailViewPath } from "src/merchantgroups/views/MerchantGroupPaths";
import { MerchantName } from "src/merchants/formatters/MerchantFormatters";
import { useMerchants } from "src/merchants/hooks/useMerchants";
import "./MerchantGroupTable.css";
import type { Merchant } from "src/merchants/objects/Merchant";
import { TemporalSort } from "src/shared/components/data-table/RaangeDataSortStrategies";
import { RaangeDataTable } from "src/shared/components/data-table/RaangeDataTable";
import RaangeDataTableSort from "src/shared/components/data-table/RaangeDataTableSort";
import {
  ContactContactName,
  ContactLanguage,
} from "src/shared/contacts/formatters/ContactFormatters";
import FormattedInstant from "src/shared/formatters/FormattedInstant";

export interface MerchantGroupTableProps {
  merchantGroups: MerchantGroup[];
  merchants?: Merchant[];
}

export function MerchantGroupTable({
  merchantGroups,
}: MerchantGroupTableProps) {
  const { data: merchants } = useMerchants();
  return (
    <RaangeDataTable<MerchantGroup>
      className="merchant-group-table"
      responsive
      data={merchantGroups}
    >
      {(processedData) => (
        <>
          <thead>
            <tr>
              <th>
                <RaangeDataTableSort<MerchantGroup> sortKey="merchantGroupName">
                  <FormattedMessage id="merchant_groups.name" />
                </RaangeDataTableSort>
              </th>
              <th>
                <RaangeDataTableSort<MerchantGroup>
                  sortKey="lastUpdated"
                  sortStrategy={TemporalSort}
                >
                  <FormattedMessage id="contacts.last_updated" />
                </RaangeDataTableSort>
              </th>
              <th>
                <FormattedMessage id="contacts.name" />
              </th>
              <th>
                <FormattedMessage id="contacts.language" />
              </th>
              <th>
                <FormattedMessage id="merchant_groups.merchants" />
              </th>
              <th>
                <FormattedMessage id="merchant_groups.status" />
              </th>
              <th />
            </tr>
          </thead>

          <tbody>
            {processedData.map((merchantGroup) => (
              <MerchantGroupTableRow
                key={merchantGroup.merchantGroupUUID}
                merchantGroup={merchantGroup}
                merchants={merchants}
              />
            ))}
          </tbody>
        </>
      )}
    </RaangeDataTable>
  );
}

interface IMerchantGroupTableRowProps {
  merchantGroup: MerchantGroup;
  merchants?: Merchant[];
}

interface IMerchantGroupTableRowsState {
  isOpen: boolean;
}

class MerchantGroupTableRow extends Component<
  IMerchantGroupTableRowProps,
  IMerchantGroupTableRowsState
> {
  public state = {
    isOpen: false,
  };

  constructor(props: IMerchantGroupTableRowProps) {
    super(props);

    this.onToggle = this.onToggle.bind(this);
  }

  public onToggle(e: MouseEvent<HTMLAnchorElement>): void {
    e.preventDefault();

    this.setState({
      isOpen: !this.state.isOpen,
    });
  }

  public render(): ReactNode {
    const { merchantGroup, merchants = null } = this.props;
    const { isOpen } = this.state;

    let merchantsForGroup: Merchant[];
    if (merchants !== null) {
      merchantsForGroup = merchants.filter(
        (merchant) =>
          merchant.merchantGroupUUID === merchantGroup.merchantGroupUUID,
      );
    } else {
      merchantsForGroup = [];
    }
    return (
      <>
        <tr key={merchantGroup.merchantGroupUUID}>
          <th>
            <MerchantGroupLogo size={2}>{merchantGroup}</MerchantGroupLogo>
            <MerchantGroupMerchants hasLink>
              {merchantGroup}
            </MerchantGroupMerchants>
          </th>
          <td>
            <FormattedInstant value={merchantGroup.lastUpdated} />
          </td>
          <td>
            <ContactContactName>{merchantGroup}</ContactContactName>
          </td>
          <td>
            <ContactLanguage>{merchantGroup}</ContactLanguage>
          </td>
          {merchants !== null && <td>{merchantsForGroup.length}</td>}
          <td>
            <MerchantGroupStatus>{merchantGroup}</MerchantGroupStatus>
          </td>
          <td className="raange-data-table-icons">
            {merchantsForGroup.length !== 0 ? (
              <a href="###" onClick={this.onToggle}>
                {isOpen ? (
                  <i className="fas fa-fw fa-chevron-down" />
                ) : (
                  <i className="fas fa-fw fa-chevron-right" />
                )}
              </a>
            ) : null}

            <Link to={getMerchantGroupDetailViewPath(merchantGroup)}>
              <i className="fas fa-fw fa-info-circle" />
            </Link>
          </td>
        </tr>

        {isOpen && merchantsForGroup.length > 0 && (
          <MerchantsRow>
            <th>
              <FormattedMessage id="merchant_groups.merchants" />
            </th>
            <td colSpan={7}>
              <ul>
                {merchantsForGroup.map((merchant) => (
                  <li key={merchant.merchantUUID}>
                    <MerchantName hasLink merchantGroup={merchantGroup}>
                      {merchant}
                    </MerchantName>
                  </li>
                ))}
              </ul>
            </td>
          </MerchantsRow>
        )}
      </>
    );
  }
}

const MerchantsRow = styled.tr`
  td,
  th {
    border-top: 1px solid rgb(218, 219, 223) !important;
  }
`;
